import React from "react";
import { Link } from "react-router-dom";
import Logo from "../../media/LogoHD.png";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { PiSparkleFill, PiEnvelopeSimpleBold, PiMapPinBold } from "react-icons/pi";
import { useLang } from "../../i18n/LanguageContext";
import { UserAuth } from "../../context/AuthContext";
import { LEGAL, SOCIAL } from "../../legal/config";

// Réseaux sociaux : seuls ceux renseignés dans legal/config.js sont affichés
const NETWORKS = [
  { key: "facebook", label: "Facebook", Icon: FaFacebookF },
  { key: "instagram", label: "Instagram", Icon: FaInstagram },
  { key: "linkedin", label: "LinkedIn", Icon: FaLinkedinIn },
].filter((n) => SOCIAL[n.key]);

const Column = ({ title, children }) => (
  <div>
    <h2 className="text-prune font-bold text-xs uppercase tracking-wider mb-3 font-sans">{title}</h2>
    <ul className="space-y-0.5 text-sm text-stone-600">{children}</ul>
  </div>
);

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="inline-block py-1 hover:text-prune hover:underline underline-offset-2">
      {children}
    </Link>
  </li>
);

// flush : collé à la section du dessus (page d'accueil, pas d'espace blanc)
const Footer = ({ flush = false }) => {
  const { t } = useLang();
  const { user } = UserAuth() || {};
  const loggedIn = !!user?.hasProfile;

  return (
    <footer className={`bg-creme ${flush ? "pt-2" : "border-t border-stone-200 mt-16"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-10 grid grid-cols-2 md:grid-cols-[1.5fr_1fr_1fr_1.2fr] gap-x-6 gap-y-6 md:gap-y-8">
        {/* Marque */}
        <div className="col-span-2 md:col-span-1 flex flex-col items-start gap-3">
          <Link to="/" aria-label={t("Tatie Soso — accueil")}>
            <img src={Logo} alt="Tatie Soso" className="h-12 w-auto" />
          </Link>
          <p className="flex items-center gap-1.5 text-stone-600 italic text-sm">
            <PiSparkleFill className="text-or-dark shrink-0" aria-hidden="true" />
            {t("Que la magie opère...")}
          </p>
          {NETWORKS.length > 0 && (
            <div className="flex gap-3 mt-1">
              {NETWORKS.map(({ key, label, Icon }) => (
                <a
                  key={key}
                  href={SOCIAL[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full bg-prune text-white flex items-center justify-center hover:bg-prune-dark transition-colors"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          )}
        </div>

        <Column title={t("Navigation")}>
          <FooterLink to="/">{t("Accueil")}</FooterLink>
          <FooterLink to="/cours_a_venir">{t("Cours à venir")}</FooterLink>
          <FooterLink to="/professeurs">{t("Nos professeurs")}</FooterLink>
          {loggedIn ? (
            <FooterLink to="/portefeuille">{t("Portefeuille")}</FooterLink>
          ) : (
            <FooterLink to="/signin">{t("Devenir prof")}</FooterLink>
          )}
        </Column>

        <Column title={t("Informations")}>
          <FooterLink to="/mentions-legales">{t("Mentions légales")}</FooterLink>
          <FooterLink to="/cgv">{t("Conditions générales")}</FooterLink>
          <FooterLink to="/confidentialite">{t("Confidentialité")}</FooterLink>
        </Column>

        <div className="col-span-2 md:col-span-1">
          <Column title={t("Contact")}>
            {LEGAL.contactEmail && (
              <li>
                <a
                  href={`mailto:${LEGAL.contactEmail}`}
                  className="inline-flex items-center gap-2 py-1 hover:text-prune hover:underline underline-offset-2 break-all"
                >
                  <PiEnvelopeSimpleBold className="text-prune shrink-0" aria-hidden="true" />
                  {LEGAL.contactEmail}
                </a>
              </li>
            )}
            <li className="flex items-center gap-2 py-1">
              <PiMapPinBold className="text-prune shrink-0" aria-hidden="true" />
              {t("Bruxelles, Belgique")}
            </li>
          </Column>
        </div>
      </div>

      <div className="border-t border-stone-200 py-4 px-4 text-center text-sm text-stone-600">
        © {new Date().getFullYear()} Tatie Soso — {t("Tous droits réservés")}
      </div>
    </footer>
  );
};

export default Footer;
